import { FILE_TYPE } from '../../../constants/file-image-types';

export function isBriefLinkedFolder(item) {
    return !!(
        item
        && item.type === FILE_TYPE.FOLDER
        && item.brief_id
        && item.brief_folder_sync !== false
    );
}

// The preview drops the file straight into an <img>, so anything the browser cannot decode is out.
const PREVIEWABLE_TYPES = [
    FILE_TYPE.IMAGE,
    FILE_TYPE.JPG,
    FILE_TYPE.JPEG,
    FILE_TYPE.PNG,
    FILE_TYPE.GIF,
    FILE_TYPE.WEBP,
    FILE_TYPE.AVIF,
    FILE_TYPE.BMP,
    FILE_TYPE.JPG_FROM_PSD,
];

export function isPreviewableImage(item) {
    return PREVIEWABLE_TYPES.includes(item?.type);
}

// Same constraint for a file picked from disk: it ends up in an <img>, so TIFF/PSD/HEIC never render.
const PLACEABLE_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif', 'bmp', 'svg'];

const PLACEABLE_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/gif',
    'image/avif',
    'image/bmp',
    'image/svg+xml',
];

export const PLACEABLE_IMAGE_LABEL = 'PNG, JPG, WEBP, GIF, AVIF, BMP or SVG';

export const PLACEABLE_IMAGE_ACCEPT = [
    ...PLACEABLE_IMAGE_EXTENSIONS.map((ext) => `.${ext}`),
    ...PLACEABLE_IMAGE_MIME_TYPES,
].join(',');

export function isPlaceableImageFile(file) {
    if (PLACEABLE_IMAGE_MIME_TYPES.includes((file?.type || '').toLowerCase())) {
        return true;
    }

    // Browsers disagree on BMP and PSD-flattened JPGs (image/x-ms-bmp, application/octet-stream).
    const ext = (file?.name || '').split('.').pop().toLowerCase();

    return PLACEABLE_IMAGE_EXTENSIONS.includes(ext);
}

// Capped by what the picker can list (IMAGE_TYPES_QUERY): svg and tiff upload fine and are then
// invisible in the grid. `psb` is a knowing exception, every other PSD path in the app accepts it.
const UPLOADABLE_IMAGE_EXTENSIONS = [
    ...PLACEABLE_IMAGE_EXTENSIONS.filter((ext) => ext !== 'svg'),
    'psd',
    'psb',
];

const UPLOADABLE_IMAGE_MIME_TYPES = [
    ...PLACEABLE_IMAGE_MIME_TYPES.filter((mime) => mime !== 'image/svg+xml'),
    'image/vnd.adobe.photoshop',
];

export const UPLOADABLE_IMAGE_LABEL = 'PNG, JPG, WEBP, GIF, AVIF, BMP, PSD or PSB';

export const UPLOADABLE_IMAGE_ACCEPT = [
    ...UPLOADABLE_IMAGE_EXTENSIONS.map((ext) => `.${ext}`),
    ...UPLOADABLE_IMAGE_MIME_TYPES,
].join(',');

export function isUploadableImageFile(file) {
    if (UPLOADABLE_IMAGE_MIME_TYPES.includes((file?.type || '').toLowerCase())) {
        return true;
    }

    const ext = (file?.name || '').split('.').pop().toLowerCase();

    return UPLOADABLE_IMAGE_EXTENSIONS.includes(ext);
}

export function splitUploadableFiles(files) {
    const list = Array.from(files || []);

    return {
        accepted: list.filter(isUploadableImageFile),
        rejected: list.filter((file) => !isUploadableImageFile(file)),
    };
}

export function uploadRejectMessage(rejected) {
    const subject = rejected.length === 1
        ? `"${rejected[0]?.name}"`
        : `${rejected.length} files`;

    return `${subject} skipped. Only ${UPLOADABLE_IMAGE_LABEL} can be uploaded here.`;
}

// The Layouts tab emits onLayoutLayers, and its layers are File objects rather than URLs.
export function layoutLayerFiles(payload) {
    const images = Array.isArray(payload?.images) ? payload.images : [];

    return images.map((image) => image?.originalFile).filter(Boolean);
}

// Keep this order: five callers rely on `url` winning over the raw key in `original_path`.
export function resolveLibraryItemUrl(item) {
    const raw = item?.path || item?.full_url || item?.url || item?.original_path || '';
    if (!raw) {
        return '';
    }
    if (/^https?:\/\//.test(raw) || raw.startsWith('/')) {
        return raw;
    }

    return `/share?file=${raw}`;
}

export function libraryItemExtension(item) {
    return resolveLibraryItemUrl(item)
        .split('.')
        .pop()
        .toLowerCase()
        .replace(/[^a-z0-9].*$/, '');
}

export function getFileDisplayName(item) {
    let extension = '';

    if (item?.asset_type === 'bynder') {
        extension = '.' + item.extension;
    } else if (item?.type === 'link' || item?.asset_type === 'salsify') {
        extension = '';
    } else {
        extension = item?.original_path
            ? '.' + item.original_path.split('.').pop()
            : '';
    }

    // Salsify can return a numeric name (UPC/GTIN), and a Number has no string methods (POR-13271).
    const name = item?.name == null ? '' : String(item.name);
    if (!name) {
        return '';
    }

    return name.toLowerCase().endsWith(extension.toLowerCase())
        ? name
        : name + extension;
}

export function downloadFileLink(url, filename) {
    const downloadUrl = new URL(url, window.location.href);

    if (downloadUrl.origin === window.location.origin && downloadUrl.pathname.startsWith('/file/download/s3/')) {
        // Keep each redirect in its own frame: concurrent S3 redirects in the
        // main browsing context can cancel another file before it starts.
        const frame = document.createElement('iframe');
        frame.hidden = true;
        frame.setAttribute('sandbox', 'allow-downloads allow-same-origin');
        frame.src = downloadUrl.href;
        document.body.appendChild(frame);

        window.setTimeout(() => frame.remove(), 5 * 60 * 1000);

        return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export const isProcessingError = (item) => {
    if (!item?.is_processing) {
        return false;
    }

    if (!item?.created_at_timestamp) {
        return false;
    }

    const createdAt = new Date(item.created_at_timestamp).getTime();
    const now = new Date().getTime();

    const hoursSinceCreate = (now - createdAt) / (1000 * 60 * 60);

    return hoursSinceCreate > 1;
}

const PAGE_STACK_QUERY_KEY = 'pageStack';

function normalizePageValue(page) {
    const n = Number(page);
    if (!Number.isFinite(n) || n < 1) {
        return '1';
    }
    return String(Math.floor(n));
}

/** Keep the current page inside 1…lastPage after items-per-page changes. */
export function clampPage(page, totalCount, pageSize) {
    const current = Number(page);
    const safePage = Number.isFinite(current) && current >= 1 ? Math.floor(current) : 1;

    if (!totalCount || !pageSize) {
        return safePage;
    }

    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    return Math.min(safePage, lastPage);
}

export function parsePageStack(query = {}) {
    const raw = query?.[PAGE_STACK_QUERY_KEY];
    if (!raw || typeof raw !== 'string') {
        return [];
    }
    return raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(normalizePageValue);
}

export function withPushedPage(query = {}, pageToPush) {
    const stack = parsePageStack(query);
    stack.push(normalizePageValue(pageToPush));

    return {
        ...query,
        page: '1',
        [PAGE_STACK_QUERY_KEY]: stack.join(','),
    };
}

export function withPoppedPage(query = {}) {
    const stack = parsePageStack(query);
    const restored = stack.pop();

    const next = { ...query };
    next.page = restored || '1';

    if (stack.length) {
        next[PAGE_STACK_QUERY_KEY] = stack.join(',');
    } else {
        delete next[PAGE_STACK_QUERY_KEY];
    }

    return next;
}
